import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gamificationApi } from '../api';
import TaskList from './TaskList';
import AchievementList from './AchievementList';

interface UserStats {
  id: number;
  total_exp: number;
  user_level: number;
  total_people: number;
  total_diaries: number;
  total_analyses: number;
  total_relationships: number;
  login_streak: number;
  exp_to_next_level: number;
  level_progress: number;
}

interface ExpLog {
  id: number;
  action_type: string;
  exp_gained: number;
  description: string;
  created_at: string;
}

interface GamificationDashboardProps {
  onClose: () => void;
}

const GamificationDashboard: React.FC<GamificationDashboardProps> = ({ onClose }) => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [expLogs, setExpLogs] = useState<ExpLog[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'achievements' | 'tasks'>('overview');
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        gamificationApi.getStats(),
        gamificationApi.getExpLogs(10),
      ]);
      setStats(statsRes.data || {});
      setExpLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  }, []);

  const initAndLoadData = useCallback(async () => {
    try {
      setLoading(true);
      // 首先初始化成就系统
      if (!initializedRef.current) {
        await gamificationApi.init();
        initializedRef.current = true;
      }
      // 加载仪表板数据
      await loadDashboardData();
    } catch (error) {
      console.error('初始化失败:', error);
    } finally {
      setLoading(false);
    }
  }, [loadDashboardData]);

  useEffect(() => {
    void initAndLoadData();
  }, [initAndLoadData]);

  const getActionTypeLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      'add_person': '添加人员',
      'write_diary': '写日记',
      'analyze_person': '性格分析',
      'create_relationship': '创建关系',
      'complete_task': '完成任务',
      'daily_login': '每日登录',
    };
    return labels[actionType] || actionType;
  };

  if (loading) {
    return (
      <div className="gamification-dashboard">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="gamification-dashboard">
      <div className="dashboard-header">
        <h2>🎮 游戏中心</h2>
        <button className="btn btn-secondary" onClick={onClose}>关闭</button>
      </div>

      {/* 用户等级卡片 */}
      {stats && (
        <div className="level-card">
          <div className="level-info">
            <div className="level-badge">
              <span className="level-number">Lv.{stats.user_level}</span>
            </div>
            <div className="level-details">
              <div className="exp-info">
                <span>总经验值: {stats.total_exp} EXP</span>
                <span>距下一级: {stats.exp_to_next_level} EXP</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${stats.level_progress}%` }}
                />
              </div>
              <div className="progress-text">{stats.level_progress}%</div>
            </div>
          </div>
          
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-icon">👥</span>
              <span className="stat-value">{stats.total_people}</span>
              <span className="stat-label">人员</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">📝</span>
              <span className="stat-value">{stats.total_diaries}</span>
              <span className="stat-label">日记</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔍</span>
              <span className="stat-value">{stats.total_analyses}</span>
              <span className="stat-label">分析</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🔗</span>
              <span className="stat-value">{stats.total_relationships}</span>
              <span className="stat-label">关系</span>
            </div>
          </div>
        </div>
      )}

      {/* 标签页 */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 概览
        </button>
        <button
          className={`tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          🏆 成就
        </button>
        <button
          className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          📋 任务
        </button>
      </div>

      {/* 概览标签页 */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="section">
            <h3>📜 最近活动</h3>
            {expLogs.length > 0 ? (
              <div className="exp-log-list">
                {expLogs.map((log) => (
                  <div key={log.id} className="exp-log-item">
                    <div className="log-info">
                      <span className="log-action">{getActionTypeLabel(log.action_type)}</span>
                      <span className="log-desc">{log.description}</span>
                    </div>
                    <div className="log-exp">+{log.exp_gained} EXP</div>
                    <div className="log-time">
                      {new Date(log.created_at).toLocaleString('zh-CN')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">暂无活动记录，开始使用应用来获得经验值吧！</p>
            )}
          </div>
        </div>
      )}

      {/* 成就标签页 */}
      {activeTab === 'achievements' && (
        <div className="tab-content">
          <AchievementList />
        </div>
      )}

      {/* 任务标签页 */}
      {activeTab === 'tasks' && (
        <div className="tab-content">
          <TaskList onTaskComplete={loadDashboardData} />
        </div>
      )}

      <style>{`
        .gamification-dashboard {
          background: white;
          border-radius: 12px;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .dashboard-header h2 {
          margin: 0;
          color: #333;
        }

        .level-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          padding: 20px;
          color: white;
          margin-bottom: 20px;
        }

        .level-info {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 20px;
        }

        .level-badge {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .level-number {
          font-size: 24px;
          font-weight: bold;
        }

        .level-details {
          flex: 1;
        }

        .exp-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .progress-bar {
          height: 12px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 6px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: #ffd700;
          border-radius: 6px;
          transition: width 0.3s ease;
        }

        .progress-text {
          text-align: right;
          font-size: 12px;
          margin-top: 4px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
        }

        .stat-item {
          text-align: center;
          background: rgba(255, 255, 255, 0.15);
          padding: 12px;
          border-radius: 8px;
        }

        .stat-icon {
          font-size: 24px;
          display: block;
          margin-bottom: 5px;
        }

        .stat-value {
          font-size: 24px;
          font-weight: bold;
          display: block;
        }

        .stat-label {
          font-size: 12px;
          opacity: 0.9;
        }

        .tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          border-bottom: 2px solid #eee;
          padding-bottom: 10px;
        }

        .tab {
          padding: 10px 20px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 16px;
          color: #666;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .tab:hover {
          background: #f5f5f5;
        }

        .tab.active {
          background: #667eea;
          color: white;
        }

        .section {
          margin-bottom: 20px;
        }

        .section h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .exp-log-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .exp-log-item {
          display: flex;
          align-items: center;
          padding: 12px;
          background: #f9f9f9;
          border-radius: 8px;
          gap: 15px;
        }

        .log-info {
          flex: 1;
        }

        .log-action {
          font-weight: 500;
          color: #333;
          display: block;
        }

        .log-desc {
          font-size: 13px;
          color: #666;
        }

        .log-exp {
          color: #4caf50;
          font-weight: bold;
          font-size: 14px;
        }

        .log-time {
          font-size: 12px;
          color: #999;
        }

        .empty-text {
          text-align: center;
          color: #999;
          padding: 20px;
        }

        @media (max-width: 600px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .level-info {
            flex-direction: column;
            text-align: center;
          }

          .exp-info {
            flex-direction: column;
            gap: 5px;
          }
        }
      `}</style>
    </div>
  );
};

export default GamificationDashboard;
